import { Children, cloneElement, ComponentType, createContext, ReactElement, ReactNode } from 'react';
import { FunctionComponent } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { 
  ResourceProps,
  ResourceMatch } from 'ra-core';
import { OdResourceOption } from '@/types/resource';

const ResourceRegister: FunctionComponent = ({
    children,
}) => {
  const childrenToRender = children as ReactElement<ResourceProps>[];
  return (
  <>
    {Children.map(
      childrenToRender,
      (child: ReactElement<ResourceProps>) =>
        cloneElement(child, {
          key: child.props.name,
          // The context prop instructs the Resource component to not render anything
          // but simply to register itself as a known resource
          intent: 'registration',
        })
    )}
  </>)
}

export interface TabMeta {
  path: string;
  label: string;
  primaryPath?: string;
}

const collectTabsMeta = (resources: ReactNode): TabMeta[] =>
  (Children.toArray(resources) as ReactElement<ResourceProps>[]
  ).filter(r => !!r.props.options).map(r => ({
    path: `/${r.props.name}`,
    label: (r.props.options && (r.props.options as OdResourceOption).label) || '',
    primaryPath: r.props.options && (r.props.options as OdResourceOption).primaryPath,
  }));


export interface WrappedComponentProps {
  tabs: TabMeta[];
  options?: OdResourceOption;
}

const withCompositeResource: (wrappedComponent: ComponentType<WrappedComponentProps>) => FunctionComponent<CompositeResourceProps> = (WrappedComponent) => ({
    intent = 'route',
    name,
    options,
    children,
    fallbackTo,
    ...props
}) =>
    intent === 'registration' ? (
        <ResourceRegister {...props} children={children} />
    ) : (
        <WrappedComponent 
          tabs={collectTabsMeta(children as ReactElement<ResourceProps>[])}
          options={options}
          {...props} 
        >
          <Switch>
            {Children.map(children as ReactElement<ResourceProps>[], (child: ReactElement<ResourceProps>) => (
              <Route 
                key={child.props.name}
                path={`/${child.props.name}`}
                render={props =>
                  cloneElement(child, {
                    // The context prop instruct the Resource component to
                    // render itself as a standard component
                    intent: 'route',
                    ...props,
                  })
                }
              />
            ))}
            <FallbackToRouteContext.Consumer>
              {(ctxFallbackto) => {
                const finalFallbackto = ctxFallbackto ?? fallbackTo
                return (
                  !finalFallbackto ? null :
                  <Route path={`/${name}`} >
                    <Redirect to={finalFallbackto} />
                  </Route>
                )
              }}
            </FallbackToRouteContext.Consumer>
          </Switch>
        </WrappedComponent>
    );

export default withCompositeResource;

export interface CompositeResourceProps {
  intent?: 'route' | 'registration';
  match?: ResourceMatch;
  name: string;
  options?: OdResourceOption;
  fallbackTo?: string;
}

export const FallbackToRouteContext = createContext((null as any) as string)
